/**
 * Error Handling Utility
 * Centralized error handling with logging and user notifications
 */

import type { ApiError, ErrorSeverity } from '../types.ts';
import { toast } from 'react-hot-toast';

/**
 * Custom Application Error Class
 */
export class AppError extends Error {
  public code: string;
  public severity: ErrorSeverity;
  public details?: Record<string, any>;

  constructor(
    code: string,
    message: string,
    severity: ErrorSeverity = 'medium',
    details?: Record<string, any>
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.severity = severity;
    this.details = details;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

/**
 * Error codes enum for type-safety
 */
export enum ErrorCode {
  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  API_ERROR = 'API_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  
  // Authentication errors
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  
  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  
  // Resource errors
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  
  // File errors
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE = 'UNSUPPORTED_FILE_TYPE',
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  DOWNLOAD_FAILED = 'DOWNLOAD_FAILED',
  
  // Storage errors
  STORAGE_QUOTA_EXCEEDED = 'STORAGE_QUOTA_EXCEEDED',
  STORAGE_ACCESS_DENIED = 'STORAGE_ACCESS_DENIED',
  
  // Generic errors
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
}

/**
 * Error messages mapping
 */
const ERROR_MESSAGES: Record<string, string> = {
  [ErrorCode.NETWORK_ERROR]: 'Network error. Please check your connection.',
  [ErrorCode.API_ERROR]: 'API error. Please try again.',
  [ErrorCode.TIMEOUT_ERROR]: 'Request timed out. Please try again.',
  [ErrorCode.UNAUTHORIZED]: 'You are not authorized to perform this action.',
  [ErrorCode.FORBIDDEN]: 'Access forbidden.',
  [ErrorCode.TOKEN_EXPIRED]: 'Your session has expired. Please login again.',
  [ErrorCode.VALIDATION_ERROR]: 'Validation error. Please check your input.',
  [ErrorCode.INVALID_INPUT]: 'Invalid input. Please try again.',
  [ErrorCode.NOT_FOUND]: 'Resource not found.',
  [ErrorCode.ALREADY_EXISTS]: 'Resource already exists.',
  [ErrorCode.FILE_TOO_LARGE]: 'File is too large.',
  [ErrorCode.UNSUPPORTED_FILE_TYPE]: 'Unsupported file type.',
  [ErrorCode.UPLOAD_FAILED]: 'File upload failed.',
  [ErrorCode.DOWNLOAD_FAILED]: 'File download failed.',
  [ErrorCode.STORAGE_QUOTA_EXCEEDED]: 'Storage quota exceeded.',
  [ErrorCode.STORAGE_ACCESS_DENIED]: 'Storage access denied.',
  [ErrorCode.UNKNOWN_ERROR]: 'An unknown error occurred.',
  [ErrorCode.SERVER_ERROR]: 'Server error. Please try again later.',
};

/**
 * Get user-friendly error message
 */
export function getErrorMessage(code: string): string {
  return ERROR_MESSAGES[code] || ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR];
}

/**
 * Handle errors with logging and user notification
 */
export function handleError(error: unknown, options?: {
  showToast?: boolean;
  customMessage?: string;
  logToConsole?: boolean;
  severity?: ErrorSeverity;
}): AppError {
  const {
    showToast = true,
    customMessage,
    logToConsole = true,
    severity = 'medium',
  } = options || {};

  let appError: AppError;

  // Convert error to AppError
  if (error instanceof AppError) {
    appError = error;
  } else if (error instanceof Error) {
    appError = new AppError(
      ErrorCode.UNKNOWN_ERROR,
      customMessage || error.message,
      severity
    );
  } else if (typeof error === 'string') {
    appError = new AppError(
      ErrorCode.UNKNOWN_ERROR,
      customMessage || error,
      severity
    );
  } else {
    appError = new AppError(
      ErrorCode.UNKNOWN_ERROR,
      customMessage || 'An unknown error occurred',
      severity
    );
  }

  // Log to console in development
  if (logToConsole && process.env.NODE_ENV === 'development') {
    console.error('Error:', {
      code: appError.code,
      message: appError.message,
      severity: appError.severity,
      details: appError.details,
      stack: appError.stack,
    });
  }

  // Show toast notification
  if (showToast) {
    showErrorToast(appError);
  }

  // Log to external service (e.g., Sentry) in production
  if (process.env.NODE_ENV === 'production' && appError.severity === 'critical') {
    logToExternalService(appError);
  }

  return appError;
}

/**
 * Show error toast notification
 */
function showErrorToast(error: AppError): void {
  const message = error.message || getErrorMessage(error.code);
  
  switch (error.severity) {
    case 'critical':
    case 'high':
      toast.error(message, {
        description: error.details?.description,
        duration: 7000,
      });
      break;
    case 'medium':
      toast.error(message, {
        duration: 5000,
      });
      break;
    case 'low':
      toast.warning(message, {
        duration: 3000,
      });
      break;
  }
}

/**
 * Log error to external monitoring service
 */
function logToExternalService(error: AppError): void {
  // TODO: Integrate with Sentry, LogRocket, or similar service
  // Example:
  // Sentry.captureException(error, {
  //   tags: {
  //     errorCode: error.code,
  //     severity: error.severity,
  //   },
  //   extra: error.details,
  // });
  console.error('Critical error logged:', error);
}

/**
 * Handle API errors
 */
export function handleApiError(response: Response, data?: any): AppError {
  let errorCode: string;
  let message: string;
  let severity: ErrorSeverity = 'medium';

  switch (response.status) {
    case 400:
      errorCode = ErrorCode.VALIDATION_ERROR;
      message = data?.message || 'Invalid request';
      severity = 'low';
      break;
    case 401:
      errorCode = ErrorCode.UNAUTHORIZED;
      message = 'Authentication required';
      severity = 'high';
      break;
    case 403:
      errorCode = ErrorCode.FORBIDDEN;
      message = 'Access forbidden';
      severity = 'high';
      break;
    case 404:
      errorCode = ErrorCode.NOT_FOUND;
      message = 'Resource not found';
      severity = 'low';
      break;
    case 409:
      errorCode = ErrorCode.ALREADY_EXISTS;
      message = 'Resource already exists';
      severity = 'low';
      break;
    case 413:
      errorCode = ErrorCode.FILE_TOO_LARGE;
      message = 'File too large';
      severity = 'medium';
      break;
    case 500:
    case 502:
    case 503:
    case 504:
      errorCode = ErrorCode.SERVER_ERROR;
      message = 'Server error';
      severity = 'critical';
      break;
    default:
      errorCode = ErrorCode.API_ERROR;
      message = data?.message || 'API error occurred';
  }

  return new AppError(errorCode, message, severity, {
    status: response.status,
    statusText: response.statusText,
    data,
  });
}

/**
 * Async error boundary wrapper
 */
export async function tryCatch<T>(
  fn: () => Promise<T>,
  errorMessage?: string
): Promise<[T | null, AppError | null]> {
  try {
    const result = await fn();
    return [result, null];
  } catch (error) {
    const appError = handleError(error, {
      customMessage: errorMessage,
      showToast: true,
    });
    return [null, appError];
  }
}

/**
 * Retry function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let lastError: Error;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (i < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw handleError(lastError!, {
    customMessage: `Failed after ${maxRetries} attempts`,
  });
}
